import React from 'react';
import { useCreateAttributeTypeMutation } from '@/api/queries';
import type { CreateOutcome } from '@/utils';
import type { AttributeTypeFormValues } from './components/attributeTypeFormModal/useAttributeTypeForm';

/**
 * Creating an attribute type — the one definition, shared by the attributes
 * page and the Control Center's "Add attribute type".
 *
 * See `@/pages/brands/useCreateBrand` for why the create path lives in a hook
 * of its own. The form's values are the request body one-for-one, so there is
 * no mapping to keep in step — only the call and the fallback sentence, which
 * is the page's existing "Failed to save attribute type." rather than a new
 * create-only wording.
 *
 * A type created from the launcher has no **values** yet; those are managed per
 * type from the attributes page, which is where the confirmation's link goes.
 */
export const useCreateAttributeType = () => {
    const createMutation = useCreateAttributeTypeMutation();

    const create = React.useCallback(
        async (input: AttributeTypeFormValues): Promise<CreateOutcome> => {
            const result = await createMutation.mutateAsync(input);

            return result.success
                ? { ok: true }
                : {
                      ok: false,
                      message:
                          result.error.message ??
                          'Failed to save attribute type.',
                  };
        },
        [createMutation],
    );

    return { create, isCreating: createMutation.isPending };
};
